import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-6">这个分类可能不存在</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51chigua.com' },
      { '@type': 'ListItem', position: 2, name: category.name },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src={category.coverImage}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/65"></div>

          <div className="relative z-10 w-full h-full flex flex-col justify-end px-4 md:px-6 pb-10 md:pb-12">
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-background-200/80 text-xs mb-4 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-background-50">{category.name}</span>
              </nav>

              {/* Category Icon & Name */}
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-primary-500/90 flex items-center justify-center">
                  <i className={`${category.icon} text-background-50 text-lg`}></i>
                </span>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50">
                  {category.name}
                </h1>
              </div>

              <p className="text-background-200/80 text-sm max-w-xl leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                全部爆料 <span className="text-foreground-400 text-base font-normal">({categoryArticles.length}篇)</span>
              </h2>
              <button className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-foreground-300 text-foreground-600 text-sm hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-up-down-line"></i>
                最新发布
              </button>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    {article.isHot && (
                      <div className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        <i className="ri-fire-line text-xs"></i>
                        <span>热门</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading font-bold text-foreground-950 text-base leading-snug mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-foreground-400 text-xs">
                        <i className="ri-eye-line"></i>
                        <span>{article.heat > 10000 ? `${(article.heat / 10000).toFixed(1)}万` : article.heat}</span>
                      </div>
                      <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                    </div>
                    <div className="mt-3">
                      <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border border-foreground-300 text-foreground-700 group-hover:bg-foreground-950 group-hover:text-background-50 group-hover:border-foreground-950">
                        阅读全文
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center gap-2 mt-12">
              <button className="w-9 h-9 rounded-lg border border-background-200 flex items-center justify-center text-foreground-400 text-sm cursor-not-allowed opacity-50">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 text-sm font-medium cursor-default">1</button>
              <button className="w-9 h-9 rounded-lg border border-background-200 flex items-center justify-center text-foreground-600 text-sm hover:border-foreground-300 cursor-pointer">2</button>
              <button className="w-9 h-9 rounded-lg border border-background-200 flex items-center justify-center text-foreground-600 text-sm hover:border-foreground-300 cursor-pointer">3</button>
              <span className="text-foreground-400 text-sm">...</span>
              <button className="w-9 h-9 rounded-lg border border-background-200 flex items-center justify-center text-foreground-600 text-sm hover:border-foreground-300 cursor-pointer">8</button>
              <button className="w-9 h-9 rounded-lg border border-background-200 flex items-center justify-center text-foreground-600 text-sm hover:border-foreground-300 cursor-pointer">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}